import { Box, Checkbox, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { ReactComponent as Emoji_5 } from "assets/ratingIcons/StarIcon.svg";
import { ReactComponent as Selected_Emoji_5 } from "assets/ratingIcons/SelectedStarIcon.svg";
import Button from "components/common/Button";
import Input from "components/common/Input";
import Typography from "components/common/Typography";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getUserHltId } from "utils/CommonFunctions";

import { useTranslate } from "hooks/useTranslate";
import { isEmpty, isFunction } from "lodash";
import { feedbackQuestionCategoryTypes, responseTypes } from "utils/Types";
import { fetchFeedbackFormDetails, saveFormData, submitFeedback } from "./Feedback.slice";

export interface ChildComponentHandles {
  submitUserFeedback: () => void; // This is the method we'll expose to the parent
}

const GiveFeedback = forwardRef<ChildComponentHandles, any>((props: any, ref?) => {
  const dispatch = useAppDispatch();
  const methods = useForm<any>();
  const translate = useTranslate();
  const { control, getValues } = methods;

  const {
    saveFormDataToRedux,
    label = translate("feedback.giveFeedbackCommonLabel"),
    id,
    payloadData,
    onSkipClickHandler,
    afterSubmitHandler,
    showActionButtons,
    isCancelButton,
    setFormId
  } = props;

  const { id: existingId, type, questions } = useAppSelector((state) => state.feedback.formDetails);
  const feedbackLoader = useAppSelector((state) => state?.feedback?.feedbackLoader);

  const [open, setOpen] = useState(props.isOpen);
  const [hoveredRating, setHoveredRating] = useState<any>({});

  const handleChange = () => setOpen(!open);

  // Watch all fields to check if at least one is filled
  const watchedFields = useWatch({ control });
  const isAnyFieldFilled = watchedFields.questions?.find(
    (item: any) => (Array.isArray(item.answers) ? !isEmpty(item.answers) : !!item.answers) || item.answerText
  );

  useEffect(() => {
    if (existingId !== parseInt(id)) dispatch(fetchFeedbackFormDetails({ id }));
  }, []);

  useImperativeHandle(ref, () => {
    return {
      submitUserFeedback: submitHandler
    };
  });

  const submitHandler = async () => {
    if (isAnyFieldFilled) {
      const formData = getValues();

      const payload = {
        id: parseInt(id),
        type,
        hltId: getUserHltId(),
        ...payloadData,
        ...formData,
        questions: formData.questions.map((question: any, index: number) => {
          const { id, category, categoryId, answers } = questions[index];

          switch (categoryId) {
            case feedbackQuestionCategoryTypes["Radio Button"]: {
              question.selectedAnswerIds = question?.answers ? [Number(question?.answers)] : [];
              break;
            }

            case feedbackQuestionCategoryTypes.Rating: {
              question.selectedAnswerIds = question?.answers
                ? [answers.find((ans: any) => ans.option === question?.answers)?.id]
                : [];
              break;
            }

            case feedbackQuestionCategoryTypes.Checkbox: {
              question.selectedAnswerIds = question?.answers?.map(Number);
              break;
            }
          }

          delete question.answers;

          return {
            ...question,
            id,
            category,
            categoryId
          };
        })
      };

      if (saveFormDataToRedux) {
        dispatch(saveFormData(payload));
      } else {
        const response = await dispatch(submitFeedback(payload));
        if (response.payload.status === responseTypes.success) {
          if (isFunction(afterSubmitHandler)) afterSubmitHandler();
        }
      }
    }
  };

  // Handle emoji hover and update hoveredRating
  const handleMouseEnter = (id: number, value: any) => {
    setHoveredRating({ ...hoveredRating, [id]: value });
  };

  const handleMouseLeave = (id: number) => {
    setHoveredRating({ ...hoveredRating, [id]: 0 });
  };

  const getAnswerOptions = (question: any, index: number) => {
    const namePrefix = `questions.[${index}]`;

    switch (question.category) {
      case "Rating":
        return (
          <>
            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
              {question.question}
            </Typography>
            <Controller
              name={`${namePrefix}.answers`}
              control={methods.control}
              render={({ field }) => (
                <Box sx={{ display: "flex", gap: "16px" }}>
                  {question.answers.map((emoji: any) => (
                    <Box
                      key={emoji.option}
                      className="emojis"
                      onClick={() => field.onChange(emoji.option)}
                      onMouseEnter={() => handleMouseEnter(question.id, emoji.option)}
                      onMouseLeave={() => handleMouseLeave(question.id)}
                    >
                      {emoji.option <= (hoveredRating[question.id] || field.value) ? <Selected_Emoji_5 /> : <Emoji_5 />}
                    </Box>
                  ))}
                </Box>
              )}
            />
          </>
        );

      case "Checkbox":
        return (
          <>
            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
              {question.question}
            </Typography>
            <Controller
              name={`${namePrefix}.answers`}
              control={control}
              defaultValue={[]} // Default as an empty array (no checkboxes selected)
              render={({ field: { onChange, value } }) => (
                <>
                  {question.answers.map((option: any) => {
                    return (
                      <FormControlLabel
                        sx={{ fontSize: "14px" }}
                        control={
                          <Checkbox
                            checked={value?.includes(option.id)}
                            onChange={(e) => {
                              const newValue = e.target.checked
                                ? [...value, option.id]
                                : value.filter((val: any) => val !== option.id);
                              onChange(newValue); // Update React Hook Form with new array
                            }}
                          />
                        }
                        label={
                          <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
                            {option.option}
                          </Typography>
                        }
                      />
                    );
                  })}
                </>
              )}
            />
          </>
        );

      case "Radio Button":
        return (
          <>
            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
              {question.question}
            </Typography>
            <Controller
              name={`${namePrefix}.answers`}
              control={control}
              render={({ field }) => (
                <RadioGroup {...field}>
                  {question.answers.map((option: any) => {
                    return (
                      <FormControlLabel
                        value={parseInt(option.id)}
                        control={<Radio />}
                        label={
                          <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
                            {option.option}
                          </Typography>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              )}
            />
          </>
        );

      default:
        return (
          <Input
            label={question.question}
            name={`${namePrefix}.answerText`}
            register={methods.register}
            textVariant="body2"
            isMultiline
            maxLength={140}
            placeholder={"Sample Text"}
            sx={{ width: "100%" }}
            color={tokens.Text.General.PrimaryHeadline}
          />
        );
    }
  };

  return (
    <div className="give-feedback">
      {!feedbackLoader && (
        <>
          <Typography variant={"body1"} showBold color={tokens.Icon.General.Secondary} children={label}></Typography>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(submitHandler)}>
              <div className="give-feedback_questions">
                {questions?.map((question: any, index: number) => (
                  <div className="give-feedback_questions-item">{getAnswerOptions(question, index)}</div>
                ))}
              </div>
              {showActionButtons && (
                <div className="action-buttons">
                  <Button label={"Skip "} variant="outlined" onClick={onSkipClickHandler} />
                  <Button label={`Give Feedback`} disabled={!isAnyFieldFilled} type={"submit"} />
                </div>
              )}
              {isCancelButton && (
                <div className="action-buttons">
                  <Button label={"Cancel "} variant="outlined" onClick={() => setFormId(null)} />
                </div>
              )}
            </form>
          </FormProvider>
        </>
      )}
    </div>
  );
});

export default GiveFeedback;
